export type DisplayNumericMetric =
  | "flow"
  | "head"
  | "power"
  | "npshr"
  | "efficiency"
  | "diameter"
  | "viscosity";

const toFiniteNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const nextValue = Number(value);
  return Number.isFinite(nextValue) ? nextValue : null;
};

export const roundDisplayNumber = (value: unknown): number | null => {
  const nextValue = toFiniteNumber(value);
  return nextValue === null ? null : Math.round(nextValue);
};

export const formatDisplayMetricValue = (
  metric: DisplayNumericMetric,
  value: unknown
): string => {
  const roundedValue = roundDisplayNumber(value);

  if (roundedValue === null) {
    return "-";
  }

  return `${roundedValue}`;
};

export const formatDisplayMetricNumber = (
  metric: DisplayNumericMetric,
  value: unknown
): number | null => roundDisplayNumber(value);

export const formatPowerDisplayValue = (value: unknown): string => {
  const nextValue = toFiniteNumber(value);
  return nextValue === null ? "-" : nextValue.toFixed(2);
};

export const formatDiameterDisplayNumber = (
  value: unknown,
  unit?: string | null
): number | null => {
  const nextValue = toFiniteNumber(value);

  if (nextValue === null) {
    return null;
  }

  return unit === "inch" || unit === "in"
    ? Number(nextValue.toFixed(1))
    : Math.round(nextValue);
};

const formatAdaptiveAxisNumber = (value: number): string => {
  if (Math.abs(value - Math.round(value)) < 1e-9) {
    return `${Math.round(value)}`;
  }

  const rounded1 = Number(value.toFixed(1));
  if (Math.abs(value - rounded1) < 1e-9) {
    return `${rounded1}`;
  }

  const rounded2 = Number(value.toFixed(2));
  if (Math.abs(value - rounded2) < 1e-9) {
    return `${rounded2}`;
  }

  return `${Number(value.toFixed(3))}`;
};

export const FLOW_AXIS_SCALE_THRESHOLD = 1000;
export const FLOW_AXIS_SCALE_DIVISOR = 100;

export const shouldUseFlowAxisHundreds = (maxValue: unknown): boolean => {
  const nextValue = toFiniteNumber(maxValue);
  return nextValue !== null && Math.abs(nextValue) >= FLOW_AXIS_SCALE_THRESHOLD;
};

export const formatFlowAxisName = (
  axisName: string,
  maxValue: unknown
): string => {
  if (!shouldUseFlowAxisHundreds(maxValue)) {
    return axisName;
  }

  return `${axisName}\n*100`;
};

export const buildIntegerAxisLabelFormatter = (
  metric: DisplayNumericMetric,
  options?: {
    shouldDisplay?: (value: number, index: number) => boolean;
    scaleDivisor?: number;
  }
) =>
  (value: unknown, index: number) => {
    const nextValue = toFiniteNumber(value);

    if (nextValue === null) {
      return "";
    }

    if (options?.shouldDisplay && !options.shouldDisplay(nextValue, index)) {
      return "";
    }

    const displayValue =
      metric === "flow" && options?.scaleDivisor
        ? nextValue / options.scaleDivisor
        : nextValue;

    // Preserve fractional ticks generated by ECharts, otherwise 1.5/2/2.5 can
    // collapse into duplicate integer labels such as 2/2/3 on small flow axes.
    if (Math.abs(displayValue - Math.round(displayValue)) >= 1e-9) {
      return formatAdaptiveAxisNumber(displayValue);
    }

    return formatDisplayMetricValue(metric, displayValue);
  };

export const buildAdaptiveAxisLabelFormatter = (
  options?: {
    shouldDisplay?: (value: number, index: number) => boolean;
  }
) =>
  (value: unknown, index: number) => {
    const nextValue = toFiniteNumber(value);

    if (nextValue === null) {
      return "";
    }

    if (options?.shouldDisplay && !options.shouldDisplay(nextValue, index)) {
      return "";
    }

    return formatAdaptiveAxisNumber(nextValue);
  };

const inferMetricFromSeriesName = (seriesName?: string): DisplayNumericMetric | null => {
  if (!seriesName) {
    return null;
  }

  if (seriesName.includes("QNPSH")) {
    return "npshr";
  }

  if (seriesName.includes("QEFF")) {
    return "efficiency";
  }

  if (seriesName.includes("QP")) {
    return "power";
  }

  if (seriesName.includes("QH") || seriesName === "aux") {
    return "head";
  }

  return null;
};

export const formatTooltipAxisHeader = (axisValue: unknown): string =>
  (() => {
    const nextValue = toFiniteNumber(axisValue);
    return nextValue === null ? "-" : formatAdaptiveAxisNumber(nextValue);
  })();

export const formatTooltipSeriesValue = (
  seriesName: string | undefined,
  rawValue: unknown
): string => {
  const metric = inferMetricFromSeriesName(seriesName || undefined);
  const seriesValue = Array.isArray(rawValue) ? rawValue[1] : rawValue;

  if (!metric) {
    const roundedValue = roundDisplayNumber(seriesValue);
    return roundedValue === null ? "-" : `${roundedValue}`;
  }

  if (metric === "power") {
    return formatPowerDisplayValue(seriesValue);
  }

  return formatDisplayMetricValue(metric, seriesValue);
};
